import { useEffect, useState } from 'react'
import { CalendarCheck, Clock, FileText, MapPin, ShieldCheck } from 'lucide-react'
import { cn } from '@/lib/cn'
import { AuthShowcase } from '@/features/auth/components/AuthLayout'
import { LOGIN_COPY, LOGIN_FEATURES } from '@/features/auth/constants/loginCopy'

const FEATURE_ICONS = {
  Clock,
  CalendarCheck,
  FileText,
  ShieldCheck,
} as const

/** Illustrative 9-hour shift, anchored so "now" always sits a few hours in. */
const SHIFT_HOURS = 9
const SHIFT_LEAD_HOURS = 3

function formatHour(hour: number): string {
  return `${(((hour % 24) + 24) % 24).toString().padStart(2, '0')}:00`
}

const ROSTER = [
  { initials: 'AK', name: 'Amara Khan', status: 'Clocked in', time: 'On time', tone: 'in' },
  { initials: 'DR', name: 'Daniel Reyes', status: 'Leave approved', time: 'Today', tone: 'leave' },
  { initials: 'SM', name: 'Sana Malik', status: 'Clocked in', time: '+3 min', tone: 'in' },
] as const

function useNow(intervalMs: number): Date {
  const [now, setNow] = useState(() => new Date())
  useEffect(() => {
    const id = window.setInterval(() => setNow(new Date()), intervalMs)
    return () => window.clearInterval(id)
  }, [intervalMs])
  return now
}

/** Live-looking "today's shift" card: real local time, shift timeline, roster. */
function ShiftVignette(): React.ReactElement {
  const now = useNow(1000)
  const hours = now.getHours().toString().padStart(2, '0')
  const minutes = now.getMinutes().toString().padStart(2, '0')
  const dateLabel = now.toLocaleDateString(undefined, {
    weekday: 'long',
    day: 'numeric',
    month: 'short',
  })
  const shiftStart = now.getHours() - SHIFT_LEAD_HOURS
  const progress = (SHIFT_LEAD_HOURS * 60 + now.getMinutes()) / (SHIFT_HOURS * 60)

  return (
    <div className="login-vignette relative w-full max-w-[28rem]">
      <div className="rounded-[1.75rem] bg-white p-5 pb-10 text-[var(--public-ink)] shadow-[0_40px_90px_-30px_rgba(0,0,0,0.75)] sm:p-6 sm:pb-10">
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0">
            <p className="text-xs font-medium text-[var(--public-muted)]">{dateLabel}</p>
            <p className="mt-1 font-display text-[3.25rem] font-semibold leading-none tracking-[-0.04em] tabular-nums">
              {hours}
              <span className="login-colon text-[var(--public-border-strong)]">:</span>
              {minutes}
            </p>
          </div>
          <span className="mt-1 inline-flex shrink-0 items-center gap-1.5 rounded-full bg-[var(--public-soft)] px-2.5 py-1 text-xs font-semibold text-primary">
            <span className="h-1.5 w-1.5 rounded-full bg-success" aria-hidden />
            On shift
          </span>
        </div>

        <p className="mt-3 flex items-center gap-1.5 text-xs text-[var(--public-muted)]">
          <MapPin className="h-3.5 w-3.5" aria-hidden />
          Day shift · Main office
        </p>

        <div className="mt-5">
          <div className="relative h-2 rounded-full bg-[var(--public-soft)]">
            <div
              className="absolute inset-y-0 left-0 rounded-full bg-primary"
              style={{ width: `${progress * 100}%` }}
            />
            <span
              className="absolute top-1/2 h-3.5 w-3.5 -translate-x-1/2 -translate-y-1/2 rounded-full border-[3px] border-white bg-accent shadow-[0_2px_6px_rgba(2,2,2,0.25)]"
              style={{ left: `${progress * 100}%` }}
              aria-hidden
            />
          </div>
          <div className="mt-2 flex justify-between text-[0.7rem] font-medium text-[var(--public-muted)] tabular-nums">
            <span>{formatHour(shiftStart)}</span>
            <span>{formatHour(shiftStart + SHIFT_HOURS)}</span>
          </div>
        </div>

        <ul className="mt-5 divide-y divide-[var(--public-border)] border-t border-[var(--public-border)]">
          {ROSTER.map((person) => (
            <li key={person.name} className="flex items-center gap-3 py-2.5">
              <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[var(--public-ink)] text-[0.65rem] font-bold text-white">
                {person.initials}
              </span>
              <span className="min-w-0 flex-1">
                <span className="block truncate text-sm font-semibold leading-tight">{person.name}</span>
                <span
                  className={cn(
                    'block truncate text-xs leading-tight',
                    person.tone === 'leave' ? 'text-primary' : 'text-[var(--public-muted)]'
                  )}
                >
                  {person.status}
                </span>
              </span>
              <span className="text-xs font-medium text-[var(--public-muted)] tabular-nums">{person.time}</span>
            </li>
          ))}
        </ul>
      </div>

      <div className="login-vignette-chip absolute -bottom-6 -left-4 flex items-center gap-2.5 rounded-2xl bg-[var(--public-ink)] py-2.5 pl-2.5 pr-4 text-white shadow-[0_18px_40px_-12px_rgba(0,0,0,0.6)] ring-1 ring-white/10 sm:-left-8">
        <span className="flex h-8 w-8 items-center justify-center rounded-xl bg-accent text-white">
          <CalendarCheck className="h-4 w-4" aria-hidden />
        </span>
        <span className="text-xs leading-tight">
          <span className="block font-semibold">Leave approved</span>
          <span className="block text-white/60">2 days · Annual</span>
        </span>
      </div>
    </div>
  )
}

/**
 * Brand panel beside the sign-in form: two-tone headline, live shift vignette,
 * and the product's capabilities as a quiet list.
 */
export function LoginShowcase(): React.ReactElement {
  return (
    <AuthShowcase
      headline={LOGIN_COPY.headlineLine1}
      headlineMuted={`${LOGIN_COPY.headlineLine2}.`}
      subhead={LOGIN_COPY.subhead}
      footer={
        <ul className="grid grid-cols-1 gap-x-8 gap-y-3 xl:grid-cols-2" aria-label="Features">
          {LOGIN_FEATURES.map((feature) => {
            const Icon = FEATURE_ICONS[feature.icon]
            return (
              <li key={feature.label} className="flex items-center gap-2.5 text-sm text-white/75">
                <Icon className="h-4 w-4 shrink-0 text-white/45" aria-hidden />
                <span className="min-w-0">{feature.label}</span>
              </li>
            )
          })}
        </ul>
      }
    >
      <ShiftVignette />
    </AuthShowcase>
  )
}
